import { FocusEventHandler, forwardRef, useContext, useRef, useState } from 'react';

import { app, media } from 'context';
import { Animate, Clickable, Icon, Layout, Link, Modal, Text } from 'ds';
import { useListFocus } from 'hooks';
import { getLocations } from 'lib/location';
import { OFFICE_REQUEST_PATH } from 'routes';
import { GRAY_900_HEX, ServiceableAddress, formatAddress, geocodeToAddress } from 'shared';

import ServiceRequestLocationResults, { ServicesRequestLocationResult } from './ServicesRequestLocationResults';
import { MOBILE_PROGRESS_FOOTER_HEIGHT } from './constants';

interface Props {
  address?: ServiceableAddress | null;
  placeholder: string;
  handleClose?: VoidFunction;
  onSelect: (address: ServiceableAddress) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  paddingBottom?: number;
}

const ServicesRequestLocationInput = forwardRef<HTMLInputElement, Props>(
  ({ onFocus, onSelect, placeholder, onBlur, paddingBottom, address }) => {
    const { ipLocation } = useContext(app);
    const ref = useRef<HTMLInputElement>(null);
    const origin = ipLocation || undefined;
    const [searchValue, setSearchValue] = useState<string>(address ? formatAddress(address) : '');
    const [results, setResults] = useState<ServicesRequestLocationResult[]>([]);
    const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
    const divId = 'request-services-location-input';
    const [bottomSheetIsVisible, setBottomSheetIsVisible] = useState(false);
    const bottomSheetInputRef = useRef<HTMLInputElement>(null);

    const [focusedIndex, setFocusedIndex] = useListFocus({
      divId,
      autoSelectFirstItem: searchValue.length > 0,
      size: results.length,
      handleEnter: index => {
        onLocationSelect(results[index]);
      }
    });

    const onLocationSelect = async (result: ServicesRequestLocationResult | null) => {
      if (!result) {
        return;
      }

      setSearchValue(result.display);
      setIsInputFocused(false);
      setResults([]);
      setFocusedIndex(-1);

      geocodeToAddress(result.display).then(address => {
        if (address.line1) {
          setBottomSheetIsVisible(false);
          onSelect({ ...address, id: -1, line2: null } as ServiceableAddress);
        } else {
          // TODO: handle this case? will this happen?
        }
      });
    };

    const handleChange = (value: string) => {
      setSearchValue(value);

      getLocations({
        input: value,
        origin,
        types: ['address'],
        country: ['us'],
        callback: results => {
          setResults(
            results
              .filter(r => r.types.some(t => ['premise', 'subpremise'].includes(t)))
              .slice(0, isBottomSheet ? 5 : 3)
              .map(result => ({
                display: result.description,
                placeId: result.place_id
              }))
          );
        }
      });
    };

    // useOnClickOutside(ref, hideResults);

    const { contentPaddingX } = useContext(app);
    const { xs, sm, isMobile } = useContext(media);
    const isBottomSheet = xs || sm;
    let input = (
      <input
        onFocus={e => {
          setIsInputFocused(true);
          setBottomSheetIsVisible(true);

          if (isBottomSheet) {
            setTimeout(() => {
              bottomSheetInputRef.current?.focus();
            }, 100);

            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 200);

            // <hacky></hacky>;
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 320);
          }

          onFocus?.(e);
        }}
        autoComplete="off"
        placeholder={placeholder}
        autoFocus={!isBottomSheet}
        ref={ref}
        spellCheck={false}
        id={divId}
        style={{
          borderStyle: 'none',
          fontSize: isBottomSheet ? 18 : 24,
          lineHeight: isBottomSheet ? '28px' : '36px',
          outline: 'none',
          background: 'none',
          color: GRAY_900_HEX,
          zIndex: 1,
          width: '100%',
          marginLeft: isBottomSheet ? 8 : 16
        }}
        value={searchValue}
        onChange={({ target: { value } }) => {
          // If the last selection was made by keyboard, we need to set the input to focused
          // to make sure the results are shown
          if (!isInputFocused) {
            setIsInputFocused(true);
          }
          handleChange(value);
        }}
        onBlur={onBlur}
      />
    );

    const inputContainer = (
      <Layout align="center">
        {isMobile && bottomSheetIsVisible ? (
          <Clickable onClick={() => setBottomSheetIsVisible(false)} style={{ display: 'inline-flex', padding: 16 }}>
            <Icon stroke={2} name="leftChevron" color="blue-500" size={20} />
          </Clickable>
        ) : (
          <Icon size={isBottomSheet ? 'md' : 'xl'} name="addressPinOutline" color="gray-900" />
        )}
        {input}
      </Layout>
    );
    const content = (
      <Layout paddingBottom={paddingBottom}>
        {inputContainer}
        {isMobile ? null : <Layout color="purple-gradient" height={2} marginTop={12} />}
        {isInputFocused && (
          <Layout
            position="absolute"
            top={54}
            borderRadius={16}
            boxShadow="2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)"
            color="white"
          >
            <ServiceRequestLocationResults
              results={results}
              onLocationSelect={onLocationSelect}
              searchValue={searchValue}
              focusedIndex={focusedIndex}
              showGoogleAttribution={false}
            />
          </Layout>
        )}
      </Layout>
    );

    return isBottomSheet ? (
      <>
        <Layout
          position="fixed"
          bottom={MOBILE_PROGRESS_FOOTER_HEIGHT}
          paddingX={24}
          paddingTop={24}
          paddingBottom={32}
          color="white"
          width="100%"
        >
          {inputContainer}
          <Layout color="purple-gradient" height={2} marginTop={12} />
          <Layout marginTop={32}>
            <Text size="body-sm">
              Still looking for an office?{' '}
              <Link href={`${OFFICE_REQUEST_PATH}`} size="body-sm" semibold={false}>
                We can help
              </Link>
            </Text>
          </Layout>
        </Layout>
        <Modal
          isVisible={bottomSheetIsVisible}
          paddingX={contentPaddingX}
          onClose={() => setBottomSheetIsVisible(false)}
          showCloseButton={false}
        >
          <Layout>
            <Layout paddingRight={24} paddingTop={24} paddingBottom={12} position="relative" align="center">
              <Clickable onClick={() => setBottomSheetIsVisible(false)} style={{ display: 'inline-flex', padding: 16 }}>
                <Icon name="leftChevron" color="gray-900" size={20} />
              </Clickable>
              <input
                onFocus={e => {
                  setIsInputFocused(true);
                  onFocus?.(e);
                }}
                autoComplete="off"
                placeholder={placeholder}
                autoFocus={!isBottomSheet}
                ref={bottomSheetInputRef}
                spellCheck={false}
                id={divId}
                style={{
                  borderStyle: 'none',
                  fontSize: 18,
                  lineHeight: '28px',
                  outline: 'none',
                  background: 'none',
                  color: GRAY_900_HEX,
                  height: 30,
                  zIndex: 1,
                  width: '100%',
                  marginLeft: 8
                }}
                value={searchValue}
                onChange={({ target: { value } }) => handleChange(value)}
                onBlur={onBlur}
              />
            </Layout>
            {isInputFocused && (
              <ServiceRequestLocationResults
                results={results}
                onLocationSelect={onLocationSelect}
                searchValue={searchValue}
                focusedIndex={focusedIndex}
                showGoogleAttribution={false}
                isBottomSheet
              />
            )}
          </Layout>
        </Modal>
      </>
    ) : (
      <Animate>{content}</Animate>
    );
  }
);
export default ServicesRequestLocationInput;
